<template>
  <el-dropdown
    split-button
    class="status-dropdown"
    :class="{'dropdown-hidden': isDropdownHidden}"
    :style="{
      '--background-color': statuses[applicationStatus].backgroundColor
    }"
    trigger="click"
    popper-class="status-dropdown-menu"
    placement="bottom-end"
    :disabled="verificationInProgress"
    @command="onCommand">
    <template #default>
      <span
        class="flex items-center"
        :class="statuses[applicationStatus].color">
        <i
          class="mr-6"
          :class="`icon-${statuses[applicationStatus].icon}`"/>
        {{ statuses[applicationStatus].title }}
      </span>
    </template>

    <template #dropdown>
      <el-dropdown-menu>
        <span class="dropdown-title">{{ t('action.change_status') }}</span>
        <el-dropdown-item
          v-for="(option, i) in options[applicationStatus]"
          :key="`dropdown_${i}`"
          :command="option.command"
          :disabled="option.disabled">
          <span :class="option.color">
            <i :class="`icon-${option.icon} mr-12`"/>
            {{ option.title }}
          </span>
        </el-dropdown-item>
        <template v-if="isVerifiedDisabled && isHintShow">
          <div class="sections">
            <p>
              {{ $t('pages.onboarding.business.company_details.verify.tooltip.disabled.sections') }}:
            </p>
            <ul
              v-if="emptySections.length"
              class="pl-16 list-disc">
              <li
                v-for="(section) in emptySections"
                :key="section.label">
                {{ $t(section.label) }}
              </li>
            </ul>
          </div>
        </template>
      </el-dropdown-menu>
    </template>
  </el-dropdown>
</template>

<script setup lang='ts'>
import { type Action, ElMessageBox } from 'element-plus';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { Onboarding } from '@/services/requests/onboarding/Onboarding.types';

interface ISection { label: string }

const {
  applicationStatus,
  sections,
  verificationInProgress = false,
  completedAllUserStages = false,
} = defineProps<{
  applicationStatus: Onboarding.ApplicationStatus;
  sections: string[];
  verificationInProgress?: boolean;
  completedAllUserStages?: boolean;
}>();

const emits = defineEmits(['onReject', 'onStatusChange']);

const { t } = useI18n();

function openVerify() {
  return ElMessageBox.alert(
    t('pages.onboarding.business.verification.tip'),
    t('pages.onboarding.business.verification.heading'),
    {
      showCancelButton: true,
      confirmButtonText: t('action.verify'),
      cancelButtonText: t('action.cancel'),
      cancelButtonClass: '1',
      confirmButtonClass: 'btn--primary',
      callback: (action: Action) => {
        if (action === 'confirm') {
          emits('onStatusChange', 'verified');
        }
      },
    },
  );
}
function openReject() {
  return ElMessageBox.alert(
    t('pages.onboarding.business.reject.tip'),
    t('pages.onboarding.business.reject.heading'),
    {
      showCancelButton: true,
      confirmButtonText: t('action.decline'),
      cancelButtonText: t('action.cancel'),
      cancelButtonClass: '1',
      confirmButtonClass: 'btn--warning',
      callback: (action: Action) => {
        if (action === 'confirm') {
          emits('onReject');
        }
      },
    },
  );
}
function onNoResponse() {
  return emits('onStatusChange', 'no-response');
}
function onInProgress() {
  return emits('onStatusChange', 'in-progress');
}
function onOnboarding() {
  return emits('onStatusChange', 'onboarding');
}
function onNonVerified() {
  return emits('onStatusChange', 'non-verified');
}
function openRevertDecline() {
  return ElMessageBox.alert(
    t('pages.onboarding.business.revert_decline.tip'),
    t('pages.onboarding.business.revert_decline.heading'),
    {
      showCancelButton: true,
      confirmButtonText: t('action.revert_to_onboarding'),
      cancelButtonText: t('action.cancel'),
      cancelButtonClass: '1',
      confirmButtonClass: 'btn--primary',
      callback: (action: Action) => {
        if (action === 'confirm') {
          emits('onStatusChange', 'onboarding');
        }
      },
    },
  );
}
function onCommand(command: { action: () => void }) {
  command.action();
}

const wasNonVerified = computed(() => completedAllUserStages);

const isVerifiedDisabled = computed(() => sections && sections.length > 0);
const emptySections = computed<ISection[]>(() => sections.map((label) => ({ label })));
const statuses = computed(() => ({
  VERIFIED: {
    title: t('entity.ApplicationStatus.VERIFIED'),
    icon: 'verified',
    disabled: isVerifiedDisabled.value,
    color: isVerifiedDisabled.value ? 'text-old-n-black-50' : 'text-old-stroke-success-default',
    command: { action: openVerify },
    backgroundColor: '#F1F8F5',
  },
  NON_VERIFIED: {
    title: t('entity.ApplicationStatus.NON_VERIFIED'),
    icon: 'non-verified',
    disabled: false,
    color: 'text-old-text-warning-default',
    command: { action: onNonVerified },
    backgroundColor: '#FDF4EE',
  },
  VERIFYING: {
    title: t('entity.ApplicationStatus.VERIFYING'),
    icon: 'non-verified',
    disabled: false,
    color: 'text-old-orange-90',
    command: () => {},
    backgroundColor: '#F4F5FB',
  },
  DECLINED: {
    title: t('entity.ApplicationStatus.DECLINED'),
    icon: 'declined',
    disabled: false,
    color: 'text-old-red-90',
    command: { action: openReject },
    backgroundColor: '#FFF2F1',
  },
  BLOCKED: {
    title: t('entity.ApplicationStatus.BLOCKED'),
    icon: 'blocked',
    disabled: false,
    color: 'text-old-red-90',
    command: () => {},
    backgroundColor: '#FFF2F1',
  },
  ONBOARDING: {
    title: t('entity.ApplicationStatus.ONBOARDING'),
    icon: 'pending',
    disabled: false,
    color: '',
    command: { action: onOnboarding },
    backgroundColor: '#F4F5FB',
  },
  IN_PROGRESS: {
    title: t('entity.ApplicationStatus.IN_PROGRESS'),
    icon: 'in-progress',
    disabled: false,
    color: 'text-old-blue-70',
    command: { action: onInProgress },
    backgroundColor: '#E3EEFF',
  },
  NO_RESPONSE: {
    title: t('entity.ApplicationStatus.NO_RESPONSE'),
    icon: 'no-response',
    disabled: false,
    color: 'text-old-n-black-100',
    command: { action: onNoResponse },
    backgroundColor: '#F4F5FB',
  },
  MANUAL: {
    title: t('entity.ApplicationStatus.MANUAL'),
    icon: 'manual',
    disabled: false,
    color: 'text-old-text-neutral-secondary',
    command: { action: openVerify },
    backgroundColor: '#F2F3F7',
  },
  CLOSED: {
    title: t('entity.ApplicationStatus.CLOSED'),
    icon: 'close-small',
    disabled: false,
    color: 'text-old-text-error-default',
    command: () => {},
    backgroundColor: '#FFF2F1',
  },
}));
const revertToOnboardingOption = computed(() => ({
  title: t('entity.ApplicationStatus.ONBOARDING'),
  icon: 'pending',
  disabled: false,
  color: '',
  command: { action: openRevertDecline },
}));
const options = computed<Record<Onboarding.ApplicationStatus, any[]>>(() => ({
  NON_VERIFIED: [
    statuses.value.IN_PROGRESS,
    statuses.value.DECLINED,
    statuses.value.VERIFIED,
  ],
  VERIFYING: [
    statuses.value.VERIFIED,
  ],
  VERIFIED: [],
  DECLINED: wasNonVerified.value ? [] : [revertToOnboardingOption.value],
  BLOCKED: [],
  ONBOARDING: [
    statuses.value.NO_RESPONSE,
    statuses.value.DECLINED,
  ],
  NO_RESPONSE: [
    statuses.value.ONBOARDING,
    statuses.value.DECLINED,
  ],
  IN_PROGRESS: [
    statuses.value.NON_VERIFIED,
    statuses.value.DECLINED,
    statuses.value.VERIFIED,
  ],
  MANUAL: [
    statuses.value.DECLINED,
    statuses.value.VERIFIED,
  ],
  CLOSED: [],
}));
const isDropdownHidden = computed(() => {
  if (['VERIFIED', 'BLOCKED', 'CLOSED'].includes(applicationStatus)) return true;
  if (applicationStatus === 'DECLINED' && wasNonVerified.value) return true;
  return false;
});
const isHintShow = computed(() => options.value[applicationStatus].includes(statuses.value.VERIFIED));
</script>

<style lang='scss'>
.status-dropdown {
  @apply shrink-0;
  @apply rounded-12 shadow-dropdown border-2 border-old-gray-10 #{!important};

  i::before {
    @apply text-lg;
  }

  .el-button-group {
    @apply font-medium text-base;

    button {
      @apply bg-old-gray-10 border-0;

      &::before {
        content: none;
      }
    }

    button:first-child {
      margin-right: 1px;
      background-color: var(--background-color);

      @apply rounded-l-32;
    }

    button:last-child {
      @apply rounded-r-32 bg-old-fill-neutral-secondary-default;

      &:hover {
        @apply bg-old-fill-neutral-secondary-hover;

        &[aria-expanded="true"] {
          @apply bg-old-fill-neutral-secondary-selected;
        }
      }
    }

    .el-dropdown__caret-button[aria-expanded="true"] {
      @apply bg-old-fill-neutral-secondary-selected;
    }
  }

  &-menu {
    @apply rounded-12 border-2 border-solid border-old-gray-10 shadow-dropdown #{!important};

    .dropdown-title {
      @apply font-bold text-base;
    }

    .el-dropdown-menu {
      @apply px-16 py-16 flex gap-8 flex-col w-[240px] font-main;

      &__item {
        @apply px-12 py-6 rounded-6;
        /* stylelint-disable custom-property-pattern */
        --el-dropdown-menuItem-hover-fill: "#F2F3F7";

        &:hover {
          @apply bg-old-gray-10;

          &.is-disabled {
            @apply bg-old-transparent;
          }
        }

        span {
          @apply flex items-center text-base font-medium;
        }
      }
    }

    .el-popper__arrow {
      display: none;
    }

    i::before {
      @apply text-lg;
    }

    .sections {
      @apply px-12 py-8 bg-old-red-10 rounded-8 text-sm leading-[18px];
    }
  }

  &.dropdown-hidden {
    button:first-child {
      @apply rounded-32;
    }

    button:last-child {
      @apply hidden;
    }
  }
}

</style>
